import { API_BASE_URL, request, getStoredAccessToken } from "./client";

export const getMyLeagues = async () => {
  return request("/leagues/me");
};

/**
 * Archives (or unarchives) a league for the signed-in person. Archiving is personal: the league leaves their My
 * Leagues list and they stop getting its emails, and nothing changes for the league. Owners can't archive their own
 * league. Returns { leagueId, archived, archivedAt }.
 */
export const setLeagueArchived = async (leagueId, archived) => {
  return request(`/leagues/${leagueId}/archive`, {
    method: "PUT",
    body: JSON.stringify({ archived }),
  });
};

export const getLeague = async (leagueId) => {
  return request(`/leagues/${leagueId}`);
};

export const createLeague = async (payload) => {
  return request("/leagues", {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const updateLeague = async (leagueId, payload) => {
  return request(`/leagues/${leagueId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
};

export const getRinks = async (leagueId) => {
  return request(`/leagues/${leagueId}/rinks`);
};

export const createRink = async (leagueId, payload) => {
  return request(`/leagues/${leagueId}/rinks`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const upsertParticipant = async (leagueId, payload) => {
  return request(`/leagues/${leagueId}/participants/upsert`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const getLeagueGames = async (leagueId) => {
  return request(`/leagues/${leagueId}/games`);
};

export const getNeighboringCities = async (leagueId) => {
  return request(`/leagues/${leagueId}/neighboring-cities`);
};

export const addNeighboringCity = async (leagueId, payload) => {
  return request(`/leagues/${leagueId}/neighboring-cities`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const removeNeighboringCity = async (leagueId, cityId) => {
  return request(`/leagues/${leagueId}/neighboring-cities/${cityId}`, {
    method: "DELETE",
  });
};

export const deleteRink = async (leagueId, rinkId) => {
  const token = getStoredAccessToken();
  const response = await fetch(`${API_BASE_URL}/leagues/${leagueId}/rinks/${rinkId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const payload = await response.json();
  if (response.status === 409 && payload.conflict) {
    return { conflict: true, ...payload };
  }
  if (!response.ok || !payload.ok) {
    throw new Error(payload?.error?.message || "Failed to delete rink");
  }
  return payload.data;
};

export const reassignAndRemoveRink = async (leagueId, fromRinkId, replacementRinkId) => {
  return request(`/leagues/${leagueId}/rinks/${fromRinkId}/reassign-and-remove`, {
    method: "POST",
    body: JSON.stringify({ replacementRinkId }),
  });
};

export const searchLeagues = async ({ city, state }) => {
  return request(`/leagues/search?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`);
};

export const getAllowedMembers = async (leagueId) => {
  return request(`/leagues/${leagueId}/allowed-members`);
};

export const addAllowedMember = async (leagueId, email) => {
  return request(`/leagues/${leagueId}/allowed-members`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
};

/**
 * Add many allowed emails at once.
 * @returns {Promise<{ added: number, alreadyPresent: number, members: Array }>}
 */
export const bulkAddAllowedMembers = async (leagueId, emails) => {
  return request(`/leagues/${leagueId}/allowed-members/bulk`, {
    method: "POST",
    body: JSON.stringify({ emails }),
  });
};

export const bulkRemoveAllowedMembers = async (leagueId, ids) => {
  return request(`/leagues/${leagueId}/allowed-members/bulk-delete`, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
};

export const removeAllowedMember = async (leagueId, memberId) => {
  return request(`/leagues/${leagueId}/allowed-members/${memberId}`, {
    method: "DELETE",
  });
};

export const getLeaguePriceHistory = async (leagueId) => {
  return request(`/leagues/${leagueId}/price-history`);
};

export const getLeagueParticipantsRoster = async (leagueId) => {
  return request(`/leagues/${leagueId}/participants-roster`);
};
